import { useState } from "react";
import { apiCall } from "../api";
import { usePromo } from "../utils/promo";
import { trackEvent } from "../analytics";

const inputClass = "w-full px-4 py-3 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white";

// Guest Tenant Search: looks in the real database but only says how many evaluations exist
const GuestTenantSearch = ({ onRegister }) => {
  const [searchForm, setSearchForm] = useState({ name: '', email: '', phone: '' });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const promo = usePromo();

  const handleSearch = async (e) => {
    e.preventDefault();
    const { name, email, phone } = searchForm;
    if (name.trim().length < 5 && !email && !phone) {
      setError('Escribe el nombre completo, el correo o el teléfono del inquilino.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await apiCall('/public/tenants/search', 'POST', {
        name: name || null, email: email || null, phone: phone || null,
      });
      setResult(data);
      trackEvent('guest_search', { found: data.total_ratings > 0 });
    } catch (err) {
      setError(err.detail || 'No pudimos hacer la búsqueda. Intenta de nuevo en un momento.');
    } finally {
      setLoading(false);
    }
  };

  const free = promo.free_mode;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-xl shadow-amber-200/60 border border-amber-200 p-6 sm:p-8 text-left">
        <h2 className="text-xl sm:text-2xl font-bold text-amber-900 mb-2 flex items-center">
          <svg className="w-7 h-7 mr-3 text-amber-700 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          Consulta a tu inquilino antes de rentar
        </h2>
        <p className="text-amber-800/80 mb-6 text-sm sm:text-base">Busca por nombre completo, correo o teléfono. Te decimos si otros propietarios ya lo evaluaron.</p>

        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-semibold text-amber-800 mb-2">Nombre completo</label>
              <input type="text" value={searchForm.name} className={inputClass} placeholder="Nombre y apellidos"
                onChange={(e) => setSearchForm(prev => ({ ...prev, name: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-amber-800 mb-2">Correo electrónico</label>
              <input type="email" value={searchForm.email} className={inputClass} placeholder="correo@ejemplo.com"
                onChange={(e) => setSearchForm(prev => ({ ...prev, email: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-amber-800 mb-2">Teléfono</label>
              <input type="tel" value={searchForm.phone} className={inputClass} placeholder="10 dígitos"
                onChange={(e) => setSearchForm(prev => ({ ...prev, phone: e.target.value }))} />
            </div>
          </div>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white py-3.5 px-6 rounded-lg hover:from-amber-600 hover:to-amber-700 shadow-md disabled:opacity-50 font-bold text-lg transition-colors"
          >
            {loading ? 'Buscando...' : 'Buscar'}
          </button>
        </form>
      </div>

      {result && (
        <div className="bg-white rounded-2xl shadow-lg border border-amber-200 p-6 text-center">
          {result.total_ratings > 0 ? (
            <>
              <p className="text-3xl font-bold text-amber-900 mb-2">
                {result.total_ratings} {result.total_ratings === 1 ? 'evaluación encontrada' : 'evaluaciones encontradas'}
              </p>
              {result.disputed_ratings > 0 && (
                <p className="text-sm text-gray-600 mb-2">
                  {result.disputed_ratings === 1 ? '1 está' : `${result.disputed_ratings} están`} en disputa.
                </p>
              )}
              <p className="text-amber-800/80 mb-6">
                Regístrate para ver las calificaciones, los comentarios y el historial de pagos{free ? ', gratis por tiempo limitado' : ''}.
              </p>
            </>
          ) : (
            <>
              <p className="text-2xl font-bold text-amber-900 mb-2">Aún no hay evaluaciones de esta persona</p>
              <p className="text-amber-800/80 mb-6">
                ¿Le rentaste antes? Evalúalo{free ? ' gratis' : ''} y ayuda a otros propietarios a decidir mejor.
              </p>
            </>
          )}
          <button
            onClick={onRegister}
            className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-8 py-3 rounded-lg hover:from-amber-600 hover:to-amber-700 shadow-md font-bold"
          >
            {free ? 'Obtener acceso fundador' : 'Crear mi cuenta'}
          </button>
        </div>
      )}
    </div>
  );
};

export { GuestTenantSearch };
